import {
  Alert,
  AlertTitle,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import dayjs, { Dayjs } from "dayjs";
import { DemoContainer, DemoItem } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setEndDate,
  setSelectedBrandConfigs,
  setSelectedBusinessAdvisorDestinationTableId,
  setSelectedFlowDestinationTableId,
  setSelectedProcessActions,
  setSelectedProcessSettingMode,
  setSelectedUnifierDestinationTableId,
  setStartDate,
} from "../../../../../../../../features/brandConfigurationReportSlice";

const DEFAULT_PROCESS_ACTIONS = ["Fetcher", "Unifier", "Advisor"];
const MIN_SELECTABLE_DATE = dayjs("2026-05-01");

const ProcessInfo = ({ onActionSelect, onDateValidityChange }: any) => {
  const { selectedProcessActions, startDate, endDate } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );
  const MAX_RANGE_DAYS = 90;
  const { brandDataInModal } = useSelector(
    (state: any) => state.brandConfigurationReport,
  );
  const dispatch = useDispatch();
  const selectedBrands = Array.isArray(brandDataInModal)
    ? brandDataInModal?.map((item: { brandName: string }) => item.brandName)
    : [];

  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null]>(
    () => {
      const today = dayjs();
      const start = startDate ? dayjs(startDate) : today;
      const end = endDate ? dayjs(endDate) : today;
      return [start, end];
    },
  );

  const [dateError, setDateError] = useState<string | null>(null);
  const handleDateChange = (newValue: [Dayjs | null, Dayjs | null]) => {
    setDateRange(newValue);
    const [start, end] = newValue;
    if (!start || !end) {
      setDateError("Start Date and End Date are required.");
      onDateValidityChange?.(false);
      return;
    }
    if (!start.isValid() || !end.isValid()) {
      setDateError("Please enter a valid date in DD-MM-YYYY format.");
      onDateValidityChange?.(false);
      return;
    }
    const diff = end.diff(start, "day");
    if (
      start.isBefore(MIN_SELECTABLE_DATE, "day") ||
      end.isBefore(MIN_SELECTABLE_DATE, "day")
    ) {
      setDateError(
        `Date range must start on or after ${MIN_SELECTABLE_DATE.format(
          "DD-MM-YYYY",
        )}.`,
      );
      onDateValidityChange?.(false);
    } else if (diff > MAX_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_RANGE_DAYS} days.`);
      onDateValidityChange?.(false);
    } else if (diff < 0) {
      setDateError("End date must be after start date.");
      onDateValidityChange?.(false);
    } else {
      const formattedStart = start.format("YYYY-MM-DD");
      const formattedEnd = end.format("YYYY-MM-DD");
      dispatch(setStartDate(formattedStart));
      dispatch(setEndDate(formattedEnd));
      setDateError(null);
      onDateValidityChange?.(true);
    }
  };
  const processActions = selectedProcessActions || [];
  const hasAction = (action: string) => processActions.includes(action);
  const isInvalidCombination =
    hasAction("Fetcher") &&
    ((hasAction("Unifier") && !hasAction("Advisor")) ||
      (hasAction("Advisor") && !hasAction("Unifier")));

  const getInvalidCombinationMessage = () => {
    if (hasAction("Fetcher") && hasAction("Advisor") && !hasAction("Unifier")) {
      return "Please note! You cannot run Fetcher & Advisor without Unifier";
    }
    return "Please note! You cannot run Fetcher & Unifier without Advisor";
  };

  const handleActionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.name;
    const checked = event.target.checked;
    const updatedActions = checked
      ? [...processActions, value]
      : processActions.filter((action: string) => action !== value);
    dispatch(setSelectedBrandConfigs([])); // Reset selected brand configs
    dispatch(setSelectedUnifierDestinationTableId([])); // Reset selected unifier destination table ID
    dispatch(setSelectedBusinessAdvisorDestinationTableId([]));
    dispatch(setSelectedFlowDestinationTableId([]));
    dispatch(setSelectedProcessSettingMode("By Flow"));
    dispatch(setSelectedProcessActions(updatedActions));
    onActionSelect(updatedActions); // Notify parent
  };

  // const shouldEnableFetcher =
  //   brandDataInModal.length > 0 &&
  //   brandDataInModal.every(
  //     (brand: any) =>
  //       Array.isArray(brand.row_output_table_data) &&
  //       brand.row_output_table_data.length > 0
  //   );

  useEffect(() => {
    if ((selectedProcessActions?.length ?? 0) > 0) return;
    dispatch(setSelectedProcessActions(DEFAULT_PROCESS_ACTIONS));
    onActionSelect(DEFAULT_PROCESS_ACTIONS);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onActionSelect, selectedProcessActions]);

  useEffect(() => {
    const today = dayjs();
    setDateRange([
      startDate ? dayjs(startDate) : today,
      endDate ? dayjs(endDate) : today,
    ]);
  }, [startDate, endDate]);
  return (
    <Box mt={3} mb={2}>
      <TextField
        size="small"
        fullWidth
        label="Selected Brands"
        value={selectedBrands.join(", ") || ""}
        color="secondary"
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
        disabled
      />
      <Box mt={2}>
        <Typography fontWeight={500}>Process Settings:</Typography>
        <Stack mt={1}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoContainer components={["DateRangePicker", "DateRangePicker"]}>
              <DemoItem component="DateRangePicker">
                <DateRangePicker
                  value={dateRange}
                  onChange={handleDateChange}
                  minDate={MIN_SELECTABLE_DATE}
                  shouldDisableDate={(date) => {
                    return date.isBefore(MIN_SELECTABLE_DATE, "day");
                  }}
                  localeText={{
                    start: "Start Date",
                    end: "End Date",
                  }}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      color: "secondary",
                      required: true,
                      error: !!dateError,
                      helperText: dateError,
                    },
                  }}
                />
              </DemoItem>
            </DemoContainer>
          </LocalizationProvider>
        </Stack>
      </Box>
      <Box mt={2}>
        <Typography fontWeight={500}>Process Actions: </Typography>
        <FormControl>
          <Stack flexDirection="row" flexWrap="wrap">
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  name="Fetcher"
                  checked={hasAction("Fetcher")}
                  onChange={handleActionChange}
                />
              }
              label="Fetcher"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  name="Unifier"
                  checked={hasAction("Unifier")}
                  onChange={handleActionChange}
                />
              }
              label="Unifier"
            />
            <FormControlLabel
              control={
                <Checkbox
                  color="secondary"
                  name="Advisor"
                  checked={hasAction("Advisor")}
                  onChange={handleActionChange}
                />
              }
              label="Advisor"
            />
          </Stack>

          <FormHelperText sx={{ margin: "4px 0px 0 0" }}>
            select at least one action.
          </FormHelperText>
        </FormControl>
        {isInvalidCombination && (
          <Alert
            severity="warning"
            sx={{
              mt: 2,
              color: "#ef6c00",
              border: "1px solid #ffb74d",
              backgroundColor: "#fff3e0",
              "& .MuiAlert-icon": { color: "#ff6d00" },
            }}
          >
            <AlertTitle sx={{ color: "#ef6c00", fontWeight: 500, mb: 0 }}>
              {getInvalidCombinationMessage()}
            </AlertTitle>
            Please select all 3 or run them separately.
          </Alert>
        )}
      </Box>
    </Box>
  );
};

export default ProcessInfo;
